import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  TextField,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  PhotoCamera,
  Palette,
  Wallpaper,
  Close,
  CheckCircle,
  Settings,
  ExitToApp,
  DeleteForever,
  Warning
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import { useNavigate } from 'react-router-dom';

// Fun kid-friendly themes
const themes = [
  { name: 'Ocean Blue', primary: '#2196F3', secondary: '#03A9F4', emoji: '🌊' },
  { name: 'Sunset Orange', primary: '#FF9800', secondary: '#FF5722', emoji: '🌅' },
  { name: 'Forest Green', primary: '#4CAF50', secondary: '#8BC34A', emoji: '🌲' },
  { name: 'Royal Purple', primary: '#9C27B0', secondary: '#E91E63', emoji: '👑' },
  { name: 'Bubblegum Pink', primary: '#E91E63', secondary: '#F48FB1', emoji: '💖' },
  { name: 'Sunshine Yellow', primary: '#FFC107', secondary: '#FFEB3B', emoji: '☀️' },
  { name: 'Cool Mint', primary: '#00BCD4', secondary: '#26C6DA', emoji: '🍃' },
  { name: 'Fire Red', primary: '#F44336', secondary: '#EF5350', emoji: '🔥' }
];

// Encode an SVG string as a CSS background-image data URI
const svgBg = (svg) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

const backgrounds = [
  {
    name: 'Stars',
    emoji: '⭐',
    // Real 5-pointed star polygons + small dot stars for a night sky feel
    gradient: svgBg(`<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
      <polygon points="60,8 68,38 100,38 76,57 85,88 60,70 35,88 44,57 20,38 52,38" fill="rgba(255,215,0,0.65)" />
      <polygon points="18,8 21,18 32,18 23,24 26,34 18,28 10,34 13,24 4,18 15,18" fill="rgba(255,230,100,0.55)" />
      <polygon points="100,72 102,79 110,79 104,84 106,91 100,87 94,91 96,84 90,79 98,79" fill="rgba(255,215,0,0.5)" />
      <circle cx="95" cy="12" r="2.5" fill="rgba(255,255,180,0.7)" />
      <circle cx="10" cy="95" r="1.5" fill="rgba(255,255,180,0.6)" />
      <circle cx="55" cy="105" r="1.5" fill="rgba(255,215,0,0.5)" />
    </svg>`),
    size: '120px 120px'
  },
  {
    name: 'Polka Dots',
    emoji: '🔵',
    // Visible purple dots in a grid
    gradient: 'radial-gradient(circle, rgba(156,39,176,0.5) 9px, transparent 9px)',
    size: '40px 40px'
  },
  {
    name: 'Waves',
    emoji: '🌊',
    // SVG sine wave — smooth rolling waves stacked vertically
    gradient: svgBg(`<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60">
      <path d="M0,30 C25,10 50,50 75,30 C100,10 125,50 150,30 C175,10 200,50 225,30" fill="none" stroke="rgba(33,150,243,0.55)" stroke-width="3"/>
      <path d="M0,42 C25,22 50,62 75,42 C100,22 125,62 150,42 C175,22 200,62 225,42" fill="none" stroke="rgba(33,150,243,0.35)" stroke-width="2"/>
      <path d="M0,18 C25,-2 50,38 75,18 C100,-2 125,38 150,18 C175,-2 200,38 225,18" fill="none" stroke="rgba(33,150,243,0.25)" stroke-width="1.5"/>
    </svg>`),
    size: '200px 60px'
  },
  {
    name: 'Hearts',
    emoji: '💕',
    // SVG heart using proper bezier curve path
    gradient: svgBg(`<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
      <path d="M40,62 C12,44 6,22 20,13 C28,8 38,12 40,22 C42,12 52,8 60,13 C74,22 68,44 40,62 Z" fill="rgba(233,30,99,0.55)" />
      <path d="M14,14 C8,11 6,5 10,3 C12,2 15,4 15.5,7 C16,4 19,2 21,3 C25,5 23,11 15,14 Z" fill="rgba(233,30,99,0.4)" />
    </svg>`),
    size: '80px 80px'
  },
  {
    name: 'Diagonal Stripes',
    emoji: '🎽',
    // Bold diagonal stripes — actually visible now
    gradient: 'repeating-linear-gradient(45deg, rgba(76,175,80,0.35) 0px, rgba(76,175,80,0.35) 10px, transparent 10px, transparent 24px)',
    size: 'auto'
  },
  {
    name: 'Rainbow Dots',
    emoji: '🌈',
    // Four offset colors: red, blue, green, yellow — arranged in a 2x2 stagger
    gradient: [
      'radial-gradient(circle, rgba(255,60,60,0.55) 9px, transparent 9px)',
      'radial-gradient(circle, rgba(33,150,243,0.55) 9px, transparent 9px)',
      'radial-gradient(circle, rgba(76,175,80,0.55) 9px, transparent 9px)',
      'radial-gradient(circle, rgba(255,193,7,0.55) 9px, transparent 9px)'
    ].join(', '),
    size: '60px 60px, 60px 60px, 60px 60px, 60px 60px',
    position: '0 0, 30px 30px, 0 30px, 30px 0'
  },
  {
    name: 'Sparkles',
    emoji: '✨',
    // SVG 4-pointed starburst sparkles
    gradient: svgBg(`<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
      <polygon points="40,4 43,37 76,40 43,43 40,76 37,43 4,40 37,37" fill="rgba(255,193,7,0.7)" />
      <polygon points="14,6 15.5,14 24,15 15.5,16 14,24 12.5,16 4,15 12.5,14" fill="rgba(255,215,0,0.6)" />
      <circle cx="66" cy="66" r="3.5" fill="rgba(255,215,0,0.55)" />
    </svg>`),
    size: '80px 80px'
  },
  {
    name: 'Checkerboard',
    emoji: '🏁',
    // Classic checkerboard — now visible (0.3 opacity, clean contrast)
    gradient: [
      'linear-gradient(45deg, rgba(100,100,100,0.35) 25%, transparent 25%, transparent 75%, rgba(100,100,100,0.35) 75%)',
      'linear-gradient(45deg, rgba(100,100,100,0.35) 25%, transparent 25%, transparent 75%, rgba(100,100,100,0.35) 75%)'
    ].join(', '),
    size: '40px 40px, 40px 40px',
    position: '0 0, 20px 20px'
  },
  {
    name: 'Bubbles',
    emoji: '🫧',
    // SVG circle outlines — looks like soap bubbles
    gradient: svgBg(`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
      <circle cx="27" cy="27" r="20" fill="none" stroke="rgba(33,150,243,0.5)" stroke-width="2.5" />
      <circle cx="77" cy="77" r="16" fill="none" stroke="rgba(33,150,243,0.45)" stroke-width="2" />
      <circle cx="78" cy="22" r="11" fill="none" stroke="rgba(100,200,255,0.5)" stroke-width="2" />
      <circle cx="20" cy="78" r="8" fill="none" stroke="rgba(33,150,243,0.4)" stroke-width="1.5" />
      <circle cx="52" cy="52" r="5" fill="none" stroke="rgba(33,150,243,0.35)" stroke-width="1.5" />
    </svg>`),
    size: '100px 100px'
  },
  { name: 'None', gradient: null, emoji: '⬜' }
];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function ProfileCustomization({ open, onClose, userProfile, currentUser }) {
  const { refreshProfile } = useAuth();
  // Child accounts use families/{familyId}/children/{childId} — not users/{uid}
  const isChildAccount = currentUser?.isChild || currentUser?.uid?.startsWith('child:');
  const childId = isChildAccount ? (currentUser.childId || currentUser.uid.replace('child:', '')) : null;

  // Returns the correct Firestore doc ref for profile updates
  const profileDocRef = () => {
    if (isChildAccount && currentUser.familyId && childId) {
      return doc(db, 'families', currentUser.familyId, 'children', childId);
    }
    return doc(db, 'users', currentUser.uid);
  };
  const [tabValue, setTabValue] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  const { deleteAccount } = useAuth();
  const { leaveFamily, familyData } = useFamilyId();
  const navigate = useNavigate();

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file!');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image is too large! Please choose a smaller image (max 5MB).');
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const safeUid = isChildAccount ? `child_${childId}` : currentUser.uid;
      const storageRef = ref(storage, `profile-pictures/${safeUid}/${Date.now()}_${file.name}`);
      
      setUploadProgress(30);
      await uploadBytes(storageRef, file);
      
      setUploadProgress(60);
      const photoURL = await getDownloadURL(storageRef);
      
      setUploadProgress(80);
      await updateDoc(profileDocRef(), {
        photoURL
      });
      
      setUploadProgress(100);
      setUploadSuccess(true);
      await refreshProfile();
      setTimeout(() => onClose(), 1200); // Brief moment to show success then close
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadError(`Upload failed: ${error.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleThemeChange = async (theme) => {
    try {
      await updateDoc(profileDocRef(), {
        theme: {
          primary: theme.primary,
          secondary: theme.secondary,
          name: theme.name
        }
      });
      await refreshProfile();
      onClose(); // Close dialog — changes visible immediately, no reload needed
    } catch (error) {
      console.error('Error updating theme:', error);
      alert('Failed to change theme. Try again!');
    }
  };

  const handleBackgroundChange = async (background) => {
    try {
      // Build background pattern object, excluding undefined values
      const backgroundPattern = {
        gradient: background.gradient || null,
        size: background.size || 'auto',
        name: background.name
      };
      
      // Only add position if it's defined
      if (background.position) {
        backgroundPattern.position = background.position;
      }
      
      await updateDoc(profileDocRef(), {
        backgroundPattern
      });
      await refreshProfile();
      onClose(); // Close dialog — background updates immediately, no reload needed
    } catch (error) {
      console.error('Error updating background:', error);
      alert('Failed to change background. Try again!');
    }
  };

  const handleNameChange = async () => {
    if (!displayName.trim()) return;
    
    try {
      await updateDoc(profileDocRef(), {
        displayName: displayName.trim()
      });
      await refreshProfile();
      onClose();
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update name. Try again!');
    }
  };

  const handleLeaveFamily = async () => {
    if (!confirmLeave) {
      setConfirmLeave(true);
      return;
    }

    setActionLoading(true);
    try {
      await leaveFamily();
      alert('You have left the family. You can now join a different family or create a new one.');
      onClose();
      navigate('/family-setup');
    } catch (error) {
      console.error('Error leaving family:', error);
      alert(`Failed to leave family: ${error.message}`);
    } finally {
      setActionLoading(false);
      setConfirmLeave(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setActionLoading(true);
    try {
      await deleteAccount();
      alert('Your account has been permanently deleted.');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(`Failed to delete account: ${error.message}\n\nNote: You may need to re-login if your session is old.`);
    } finally {
      setActionLoading(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">
            ✨ Customize Your Profile ✨
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<PhotoCamera />} label="Photo" />
          <Tab icon={<Palette />} label="Theme" />
          <Tab icon={<Wallpaper />} label="Background" />
          <Tab icon={<Settings />} label="Account" />
        </Tabs>

        {/* Photo Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Avatar
              src={userProfile?.photoURL}
              sx={{ width: 120, height: 120, fontSize: 48, bgcolor: 'secondary.main' }}
            >
              {!userProfile?.photoURL && userProfile?.displayName?.charAt(0)}
            </Avatar>
            
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              helperText="Change your display name"
            />
            <Button variant="contained" onClick={handleNameChange} disabled={!displayName.trim()}>
              Save Name
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={uploading ? null : <PhotoCamera />}
              disabled={uploading}
              size="large"
              fullWidth
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload New Photo 📸'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handlePhotoUpload}
              />
            </Button>

            {uploading && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" textAlign="center" display="block" sx={{ mt: 1 }}>
                  {uploadProgress < 30 && 'Starting upload...'}
                  {uploadProgress >= 30 && uploadProgress < 60 && 'Uploading photo...'}
                  {uploadProgress >= 60 && uploadProgress < 80 && 'Processing...'}
                  {uploadProgress >= 80 && uploadProgress < 100 && 'Almost done...'}
                  {uploadProgress === 100 && 'Complete! ✨'}
                </Typography>
              </Box>
            )}

            {uploadSuccess && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ width: '100%' }}>
                📸 Photo uploaded successfully! Refreshing page...
              </Alert>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {uploadError}
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose a fun photo that represents you! 📸
              <br />
              <Typography variant="caption" color="text.secondary">
                Max size: 5MB • Formats: JPG, PNG, GIF
              </Typography>
            </Typography>
          </Box>
        </TabPanel>

        {/* Theme Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
            Pick Your Favorite Color Theme! 🎨
          </Typography>
          <Grid container spacing={2}>
            {themes.map((theme) => (
              <Grid item xs={6} sm={4} md={3} key={theme.name}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  onClick={() => handleThemeChange(theme)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: '100%',
                        height: 80,
                        background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                        borderRadius: 2,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 32
                      }}
                    >
                      {theme.emoji}
                    </Box>
                    <Typography variant="body2" textAlign="center" fontWeight={600}>
                      {theme.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Background Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
            Choose Your Background Pattern! 🖼️
          </Typography>
          <Grid container spacing={2}>
            {backgrounds.map((background) => (
              <Grid item xs={6} sm={4} md={3} key={background.name}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  onClick={() => handleBackgroundChange(background)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: '100%',
                        height: 80,
                        backgroundImage: background.gradient || 'none',
                        backgroundColor: '#fff',
                        backgroundSize: background.size || 'auto',
                        backgroundPosition: background.position || '0 0',
                        borderRadius: 2,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 32,
                        border: '2px solid #ddd'
                      }}
                    >
                      {background.emoji}
                    </Box>
                    <Typography variant="body2" textAlign="center" fontWeight={600}>
                      {background.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Account Settings Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
            Account Settings
          </Typography>
          
          {/* Family Information */}
          {familyData && (
            <Card sx={{ mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600}>
                  Current Family
                </Typography>
                <Typography variant="h6">
                  {familyData.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Family Code: <strong>{familyData.inviteCode}</strong>
                </Typography>
              </CardContent>
            </Card>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Leave Family Section */}
          {familyData && (
            <Box sx={{ mb: 4 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <ExitToApp color="warning" />
                <Typography variant="h6">Leave Family</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Leave "{familyData.name}" and join a different family. Your account will not be deleted, but you'll need a new family code to join another family.
              </Typography>
              
              {!confirmLeave ? (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ExitToApp />}
                  onClick={handleLeaveFamily}
                  disabled={actionLoading}
                  fullWidth
                >
                  Leave Family
                </Button>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Are you sure you want to leave this family?
                  </Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={handleLeaveFamily}
                      disabled={actionLoading}
                    >
                      Yes, Leave
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setConfirmLeave(false)}
                      disabled={actionLoading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Delete Account Section */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Warning color="error" />
              <Typography variant="h6" color="error">Danger Zone</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete your account and all associated data. This action cannot be undone!
            </Typography>
            
            {!confirmDelete ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForever />}
                onClick={handleDeleteAccount}
                disabled={actionLoading}
                fullWidth
              >
                Delete Account
              </Button>
            ) : (
              <Alert severity="error" icon={<Warning />}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  ⚠️ This will permanently delete your account!
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                  All your data will be lost forever. This cannot be undone.
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={handleDeleteAccount}
                    disabled={actionLoading}
                  >
                    Yes, Delete Forever
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirmDelete(false)}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                </Box>
              </Alert>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProfileCustomization;

